/**
 * Guided Workflow Panel Component
 * Main container for the workflow UI with toggle visibility
 * Conditionally renders Video Workflow or Template Workflow based on conversation type
 */

import { useEffect } from 'react';
import { WorkflowTimeline } from './WorkflowTimeline';
import { TemplateWorkflowPanel } from './TemplateWorkflowPanel';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConversationStore } from '@/stores/conversationStore';
import { workflowActions } from '@/services/workflowActions';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Workflow } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WorkflowStepId } from '@/types/workflow';

// Note: workflowStore and workflowActions are auto-exposed to window for console access

interface GuidedWorkflowPanelProps {
  conversationId: string;
  className?: string;
}

export function GuidedWorkflowPanel({ conversationId, className }: GuidedWorkflowPanelProps) {
  const { getConversationById } = useConversationStore();

  // Check conversation type to determine which workflow to show
  const conversation = getConversationById(conversationId);
  const isTemplateConversation = conversation?.collectionType === 'STYLE';
  const isSlideConversation = conversation?.collectionType === 'SLIDE';

  // Hide workflow panel for slide conversations
  if (isSlideConversation) {
    return null;
  }

  // If template conversation, render TemplateWorkflowPanel instead
  if (isTemplateConversation) {
    return <TemplateWorkflowPanel conversationId={conversationId} className={className} />;
  }

  // Render video workflow
  return <VideoWorkflowPanel conversationId={conversationId} className={className} isSlideWorkflow={false} />;
}

// Video Workflow Panel (extracted from original GuidedWorkflowPanel)
function VideoWorkflowPanel({ conversationId, className, isSlideWorkflow = false }: GuidedWorkflowPanelProps & { isSlideWorkflow?: boolean }) {
  const {
    getWorkflowState,
    isWorkflowVisible,
    isWorkflowEnabled,
    setWorkflowVisibility,
    initWorkflow,
  } = useWorkflowStore();

  const { getConversationById } = useConversationStore();

  const workflowState = getWorkflowState(conversationId);
  const isVisible = isWorkflowVisible(conversationId);
  const isEnabled = isWorkflowEnabled(conversationId);

  // Check if conversation is video or slide project
  const conversation = getConversationById(conversationId);
  const isVideoConversation = conversation?.collectionType === 'VIDEO';
  const isSlideConversationCheck = conversation?.collectionType === 'SLIDE';
  const hasVideoProject = conversation?.metadata?.hasVideoProject || isVideoConversation;
  const hasSlideProject = conversation?.metadata?.hasSlideProject || isSlideConversationCheck;
  const hasProject = hasVideoProject || hasSlideProject;

  // Initialize workflow if needed
  useEffect(() => {
    if (!workflowState && hasProject) {
      console.log(`🔧 Initializing ${isSlideWorkflow ? 'slide' : 'video'} workflow for conversation:`, conversationId);
      initWorkflow(conversationId, conversation?.collectionType || 'VIDEO');
    }
  }, [conversationId, workflowState, hasProject, isSlideWorkflow, initWorkflow, conversation?.collectionType]);

  // Toggle visibility
  const handleToggleVisibility = () => {
    setWorkflowVisibility(conversationId, !isVisible);
  };

  // Handle step actions
  const handleStepAction = async (stepId: WorkflowStepId, actionId: string) => {
    console.log('🎬 Workflow action triggered:', { stepId, actionId, conversationId });

    try {
      switch (actionId) {
        case 'view_template':
          // Navigate to gallery settings tab to view templates
          await workflowActions.viewTemplate(conversationId);
          break;

        case 'generate_plan':
          await workflowActions.generatePlan(conversationId);
          break;

        case 'edit_plan':
          await workflowActions.editPlan(conversationId);
          break;

        case 'view_plan':
          await workflowActions.viewPlan(conversationId);
          break;

        case 'view_gallery':
          // Navigate to gallery scenes tab
          await workflowActions.viewGallery(conversationId);
          break;

        default:
          console.warn('Unknown workflow action:', actionId);
      }
    } catch (error) {
      console.error('❌ Workflow action failed:', error);
      // Error is already handled in action handlers
    }
  };

  // Don't render if workflow not enabled or doesn't exist
  if (!workflowState || !isEnabled) {
    return null;
  }

  return (
    <div className={cn('flex-shrink-0 bg-background border-b', className)}>
      {/* Toggle Button Bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-primary/30 bg-accent">
        <div className="flex items-center gap-2">
          <Workflow className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-900">{isSlideWorkflow ? 'Slide Workflow' : 'Video Workflow'}</span>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleVisibility}
          className="h-6 px-1.5 text-xs"
        >
          {isVisible ? (
            <>
              <EyeOff className="h-3 w-3 mr-0.5" />
              Hide
            </>
          ) : (
            <>
              <Eye className="h-3 w-3 mr-0.5" />
              Show
            </>
          )}
        </Button>
      </div>

      {/* Workflow Content - Collapsible */}
      {isVisible && (
        <div className="p-2.5 bg-background border-b border-gray-200 animate-in slide-in-from-top-2 duration-300">
          <WorkflowTimeline
            workflowState={workflowState}
            conversationId={conversationId}
            onStepAction={handleStepAction}
          />
        </div>
      )}
    </div>
  );
}
